/**
 * PackageGenerator - Risk Panel disclosure package generator (Bill 2026-10-07).
 *
 * "...so that the settings can be tweaked and packages regenerated and reviewed
 * for efficient testing without junking up carts and loading Core Director with
 * a slew of test CIFs and accounts to KILL and manage."
 *
 * Describe a test applicant, generate, review. The server builds an in-memory
 * test cart (never saved, no CIF, no account, nothing sent to Core Director) and
 * runs the same resolve + render the signing package uses. Settings and template
 * edits made elsewhere apply to the next Generate with no redeploy: the resolver
 * and renderer read templates, versions, conditions and settings from the
 * database on every run, and the merge-template cache is keyed on the body text.
 *
 * Scenarios are saved in this browser only. The previous run's fingerprints are
 * kept so the next run shows what a tweak added, removed or changed.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Columns2, Download, Eye, FileStack, Play, Save, X } from '@retail-ops/panel-shell';

import {
  loadPreviewCatalog,
  pdfObjectUrl,
  runPackagePreview,
  type PreviewCatalog,
  type PreviewDocument,
  type PreviewRun,
  type Scenario,
} from './packagePreviewApi';
import {
  PRESETS,
  blankScenario,
  diffRuns,
  docKey,
  loadLastSummary,
  loadSavedScenarios,
  storeLastSummary,
  storeSavedScenarios,
  summarize,
  type DiffRow,
  type RunSummary,
  type SavedScenario,
} from './scenario';
import { ScenarioBuilder } from './ScenarioBuilder';
import './PackageGenerator.css';

const STAGE_LABEL: Record<string, string> = {
  'product-filters': 'Product rules',
  'presence-condition': 'Condition',
  'entity-rule': 'Entity rule',
  'universal-floor': 'Always included',
};

interface Viewer {
  title: string;
  panes: Array<{ label: string; url: string }>;
}

export function PackageGenerator(): JSX.Element {
  const [params] = useSearchParams();
  const focusTemplate = params.get('template')?.trim().toUpperCase() || null;

  const [catalog, setCatalog] = useState<PreviewCatalog | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [scenario, setScenario] = useState<Scenario>(() => ({
    ...blankScenario(),
    // Arriving from a template edit: show the edit, not the live version.
    ...(focusTemplate ? { versionSource: 'includeDrafts' as const } : {}),
  }));
  const [saved, setSaved] = useState<SavedScenario[]>(() => loadSavedScenarios());
  const [run, setRun] = useState<PreviewRun | null>(null);
  const [previousRun, setPreviousRun] = useState<PreviewRun | null>(null);
  const [baseline, setBaseline] = useState<RunSummary | null>(() => loadLastSummary());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadPreviewCatalog()
      .then((c) => { if (!cancelled) setCatalog(c); })
      .catch((err: unknown) => { if (!cancelled) setCatalogError(err instanceof Error ? err.message : 'Could not load the catalog.'); });
    return () => { cancelled = true; };
  }, []);

  // Viewer object URLs are revoked when the viewer closes.
  const closeViewer = useCallback((): void => {
    setViewer((v) => {
      v?.panes.forEach((p) => URL.revokeObjectURL(p.url));
      return null;
    });
  }, []);

  const productCodes = useMemo(() => new Set(catalog?.products.map((p) => p.code) ?? []), [catalog]);
  const presets = useMemo(() => PRESETS.filter((p) => p.needs.every((c) => productCodes.has(c))), [productCodes]);

  const generate = async (): Promise<void> => {
    if (!catalog) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setBusy(true);
    setError(null);
    try {
      const byCode = new Map(catalog.products.map((p) => [p.code, p]));
      // Send only elections the catalog offers - the server refuses the rest.
      const clean: Scenario = {
        ...scenario,
        products: scenario.products.map((p) => {
          const c = byCode.get(p.code);
          return {
            ...p,
            orderChecks: p.orderChecks && (c?.checks?.offered ?? false),
            overdraft: c?.overdraft?.offered ? p.overdraft : 'none',
            beneficiaries: c?.allowBeneficiaries ? p.beneficiaries : [],
          };
        }),
      };
      const next = await runPackagePreview(clean, ac.signal);
      if (run) {
        setPreviousRun(run);
        setBaseline(summarize(run));
      }
      setRun(next);
      storeLastSummary(summarize(next));
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'The package could not be generated.');
    } finally {
      setBusy(false);
    }
  };

  const saveScenario = (): void => {
    const name = window.prompt('Name this scenario (saved in this browser only):');
    if (!name?.trim()) return;
    const list = [...saved.filter((s) => s.name !== name.trim()), { name: name.trim(), savedAt: new Date().toISOString(), scenario }];
    if (storeSavedScenarios(list)) setSaved(list);
    else setError('This browser would not save the scenario (storage is blocked or full).');
  };

  const deleteSaved = (name: string): void => {
    const list = saved.filter((s) => s.name !== name);
    storeSavedScenarios(list);
    setSaved(list);
  };

  const openDoc = (d: PreviewDocument): void => {
    if (!d.pdfBase64) return;
    closeViewer();
    setViewer({ title: d.title, panes: [{ label: `${d.templateCode} - version ${d.versionNumber}`, url: pdfObjectUrl(d.pdfBase64) }] });
  };

  const openSideBySide = (row: DiffRow): void => {
    const before = previousRun?.documents.find((d) => docKey(d) === row.key && d.pdfBase64);
    const after = run?.documents.find((d) => docKey(d) === row.key && d.pdfBase64);
    if (!before || !after) return;
    closeViewer();
    setViewer({
      title: after.title,
      panes: [
        { label: `Previous run - version ${before.versionNumber}`, url: pdfObjectUrl(before.pdfBase64) },
        { label: `This run - version ${after.versionNumber}`, url: pdfObjectUrl(after.pdfBase64) },
      ],
    });
  };

  const download = (base64: string, fileName: string): void => {
    const url = pdfObjectUrl(base64);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  const diff = useMemo(() => (run && baseline && baseline.runId !== run.runId ? diffRuns(baseline, summarize(run)) : null), [run, baseline]);

  return (
    <>
      <section className="page-header">
        <Link className="back-link" to="/">Back to Risk Management Panel</Link>
        <h1 className="page-header__title">Disclosure package generator</h1>
        <p className="page-header__subtitle">
          Describe a test applicant and see the exact package the signing step would build: which documents, why each one is
          in or out, who signs, and the PDFs. Nothing is saved, no customer or account is created, and nothing reaches Core
          Director. Change a setting or a template, then generate again to see the effect.
        </p>
      </section>

      {focusTemplate && (
        <div className="banner banner--info">
          Looking at template <strong className="pg-code">{focusTemplate}</strong>. Drafts and pending versions are included,
          so you see the edit before it is activated. Pick a scenario that should include this document and generate.
        </div>
      )}
      {catalogError && <div className="banner banner--err">{catalogError}</div>}
      {!catalog && !catalogError && <div className="empty">Loading the product and ownership catalog...</div>}

      {catalog && (
        <div className="pg-layout">
          <aside className="card pg-builder" data-tour="pg-builder" aria-label="Scenario">
            <div className="pg-section">
              <h3 className="pg-section__title">Start from</h3>
              <div className="pg-row">
                <select className="pg-select" style={{ flex: 1, minWidth: 0 }} value="" aria-label="Preset scenario"
                  onChange={(e) => {
                    const p = presets.find((x) => x.id === e.target.value);
                    if (p) setScenario({ ...p.build(), versionSource: scenario.versionSource, compilePdfs: scenario.compilePdfs });
                  }}>
                  <option value="">Phase 1 preset...</option>
                  {presets.map((p) => <option key={p.id} value={p.id} title={p.detail}>{p.label}</option>)}
                </select>
              </div>
              <div className="pg-row">
                <select className="pg-select" style={{ flex: 1, minWidth: 0 }} value="" aria-label="Saved scenario"
                  onChange={(e) => {
                    const s = saved.find((x) => x.name === e.target.value);
                    if (s) setScenario({ ...blankScenario(), ...s.scenario });
                  }}>
                  <option value="">{saved.length ? 'My saved scenarios...' : 'No saved scenarios yet'}</option>
                  {saved.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                </select>
                <button type="button" className="btn btn--ghost btn--sm" onClick={saveScenario}><Save size={14} /> Save</button>
              </div>
              {saved.length > 0 && (
                <details style={{ marginTop: 6, fontSize: 12 }}>
                  <summary>Manage saved scenarios</summary>
                  {saved.map((s) => (
                    <div className="pg-row" key={s.name}>
                      <span style={{ flex: 1 }}>{s.name}</span>
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => deleteSaved(s.name)}>Delete</button>
                    </div>
                  ))}
                </details>
              )}
            </div>

            <ScenarioBuilder catalog={catalog} scenario={scenario} onChange={setScenario} />

            <div className="pg-section">
              <h3 className="pg-section__title">Template versions</h3>
              <div className="pg-seg" role="group" aria-label="Template versions">
                <button type="button" aria-pressed={scenario.versionSource === 'active'} onClick={() => setScenario({ ...scenario, versionSource: 'active' })}>
                  Active versions
                </button>
                <button type="button" aria-pressed={scenario.versionSource === 'includeDrafts'} onClick={() => setScenario({ ...scenario, versionSource: 'includeDrafts' })}>
                  Include drafts and pending
                </button>
              </div>
              <p className="pg-hint">
                {scenario.versionSource === 'active'
                  ? 'What customers get today.'
                  : 'Uses the newest draft, in-review or approved version where one exists, otherwise the active one.'}
              </p>
            </div>

            <div className="pg-section">
              <h3 className="pg-section__title">Output</h3>
              <div className="pg-seg" role="group" aria-label="Output">
                <button type="button" aria-pressed={scenario.compilePdfs} onClick={() => setScenario({ ...scenario, compilePdfs: true })}>Documents and PDFs</button>
                <button type="button" aria-pressed={!scenario.compilePdfs} onClick={() => setScenario({ ...scenario, compilePdfs: false })}>Quick check (no PDFs)</button>
              </div>
              <p className="pg-hint">The quick check fills every document but skips printing, so it returns in a second or two.</p>
            </div>

            <div className="pg-run-bar" data-tour="pg-run">
              <button type="button" className="btn btn--primary" disabled={busy || scenario.products.length === 0} onClick={() => void generate()}>
                <Play size={14} /> {busy ? 'Generating...' : 'Generate package'}
              </button>
              {busy && <button type="button" className="btn btn--ghost btn--sm" onClick={() => abortRef.current?.abort()}>Cancel</button>}
            </div>
            {error && <div className="banner banner--err" style={{ marginTop: 12 }}>{error}</div>}
          </aside>

          <section aria-label="Package">
            {!run && (
              <div className="empty">
                <FileStack size={22} style={{ display: 'block', margin: '0 auto 8px' }} />
                Pick a preset or build a scenario, then generate. The package appears here.
              </div>
            )}
            {run && (
              <RunView run={run} diff={diff} baselineAt={baseline?.generatedAtUtc ?? null} focusTemplate={focusTemplate}
                canSideBySide={(row) => Boolean(previousRun?.documents.some((d) => docKey(d) === row.key && d.pdfBase64)
                  && run.documents.some((d) => docKey(d) === row.key && d.pdfBase64))}
                onOpen={openDoc} onSideBySide={openSideBySide} onDownload={download} />
            )}
          </section>
        </div>
      )}

      {viewer && (
        <div className="pg-viewer-backdrop" role="dialog" aria-modal="true" aria-label={viewer.title}
          onClick={(e) => { if (e.target === e.currentTarget) closeViewer(); }}>
          <div className="pg-viewer">
            <div className="pg-viewer__head">
              <h3>{viewer.title}</h3>
              <button type="button" className="btn btn--ghost btn--sm" onClick={closeViewer} aria-label="Close"><X size={16} /></button>
            </div>
            <div className={`pg-viewer__panes${viewer.panes.length > 1 ? ' pg-viewer__panes--two' : ''}`}>
              {viewer.panes.map((p) => (
                <div className="pg-viewer__pane" key={p.url}>
                  <span className="pg-viewer__pane-label">{p.label}</span>
                  {/* No sandbox: Edge refuses to show a PDF inside a sandboxed frame ("This page has been blocked
                      by Microsoft Edge", Bill 2026-10-07). The frame only ever holds a PDF this server generated. */}
                  <iframe src={p.url} title={`${viewer.title} - ${p.label}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface RunViewProps {
  run: PreviewRun;
  diff: DiffRow[] | null;
  baselineAt: string | null;
  focusTemplate: string | null;
  canSideBySide: (row: DiffRow) => boolean;
  onOpen: (d: PreviewDocument) => void;
  onSideBySide: (row: DiffRow) => void;
  onDownload: (base64: string, fileName: string) => void;
}

function RunView({ run, diff, baselineAt, focusTemplate, canSideBySide, onOpen, onSideBySide, onDownload }: RunViewProps): JSX.Element {
  const totalPages = run.documents.reduce((s, d) => s + d.pageCount, 0);
  const signing = run.documents.filter((d) => d.requiresSignature).length;
  const focusMissing = focusTemplate && !run.documents.some((d) => d.templateCode.toUpperCase() === focusTemplate);
  const focusDropped = focusTemplate ? run.dropped.find((d) => d.templateCode.toUpperCase() === focusTemplate) : undefined;

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <h2 style={{ flex: 1 }}>{run.documents.length} document{run.documents.length === 1 ? '' : 's'}</h2>
          {run.combinedPdfBase64 && (
            <button type="button" className="btn btn--sm" onClick={() => onDownload(run.combinedPdfBase64!, 'disclosure-package-preview.pdf')}>
              <Download size={14} /> Download all as one PDF
            </button>
          )}
        </div>
        <div className="counts-strip">
          <div className="counts-strip__item"><div className="counts-strip__label">Need a signature</div><div className="counts-strip__value">{signing}</div></div>
          <div className="counts-strip__item"><div className="counts-strip__label">Read only</div><div className="counts-strip__value">{run.documents.length - signing}</div></div>
          <div className="counts-strip__item"><div className="counts-strip__label">Pages</div><div className="counts-strip__value">{totalPages}</div></div>
          <div className="counts-strip__item"><div className="counts-strip__label">Generated in</div><div className="counts-strip__value">{(run.elapsedMs / 1000).toFixed(1)}s</div></div>
        </div>
        <p className="pg-hint">
          {run.versionSource === 'active' ? 'Active versions.' : 'Drafts and pending versions included.'}{' '}
          {run.compilePdfs ? '' : 'Quick check - no PDFs, page counts are the template defaults.'}{' '}
          Owners: {run.people.map((p) => `${p.name} (${p.role}${p.minor ? ', minor' : ''})`).join('; ')}.
        </p>
        <div className="banner banner--info" style={{ marginTop: 10, marginBottom: 0 }}>{run.caveat}</div>
      </div>

      {focusMissing && (
        <div className="banner banner--warn">
          <strong className="pg-code">{focusTemplate}</strong> is not in this package.
          {focusDropped ? ` Why: ${focusDropped.reason}.` : ' The resolver did not consider it for this scenario.'}
        </div>
      )}
      {run.errors.length > 0 && (
        <div className="banner banner--err">
          <strong>Problems building the package</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{run.errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      {run.notRendered.length > 0 && (
        <div className="banner banner--warn">
          Should be in the package but could not be produced: {run.notRendered.map((d) => `${d.title} (${d.templateCode})`).join(', ')}.
        </div>
      )}
      {run.scenarioNotes.map((n) => <div className="banner banner--warn" key={n}>{n}</div>)}

      {diff && <DiffCard diff={diff} baselineAt={baselineAt} canSideBySide={canSideBySide} onSideBySide={onSideBySide} />}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>The package, in signing order</h3></div>
        <ul className="pg-docs">
          {run.documents.map((d) => (
            <li key={d.instanceId} className={`pg-doc${focusTemplate && d.templateCode.toUpperCase() === focusTemplate ? ' pg-doc--focus' : ''}`}>
              <span className="pg-doc__n">{d.order}</span>
              <div style={{ minWidth: 0 }}>
                <div className="pg-doc__title">{d.title}</div>
                <div className="pg-doc__meta">
                  <span className="pg-code">{d.templateCode}{d.bodyTemplateCode ? ` (body ${d.bodyTemplateCode})` : ''}</span>
                  {d.requiresSignature ? <span className="pill pill--warn">Signature</span> : <span className="pill pill--info">Read only</span>}
                  <span>Signs: {d.signerRoles.length ? d.signerRoles.join(', ') : 'nobody'}</span>
                  <span>{d.pageCount} page{d.pageCount === 1 ? '' : 's'}{d.pageCountMeasured ? '' : ' (template default)'}</span>
                  <span>Version {d.versionNumber}</span>
                  <VersionPill status={d.versionStatus} />
                  {d.accountId && <span className="pg-code">{d.accountId}</span>}
                </div>
                {d.reasons.length > 0 && (
                  <ul className="pg-doc__why">
                    {d.reasons.map((r, i) => <li key={i}><strong>{STAGE_LABEL[r.stage] ?? r.stage}:</strong> {r.reason}</li>)}
                  </ul>
                )}
                {d.tokenWarnings.length > 0 && (
                  <details style={{ fontSize: 12, marginTop: 4 }}>
                    <summary>{d.tokenWarnings.length} fill-in warning{d.tokenWarnings.length === 1 ? '' : 's'}</summary>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{d.tokenWarnings.map((w) => <li key={w}>{w}</li>)}</ul>
                  </details>
                )}
              </div>
              <div className="pg-doc__actions">
                {d.pdfBase64 && (
                  <>
                    <button type="button" className="btn btn--sm" onClick={() => onOpen(d)}><Eye size={14} /> Preview</button>
                    <button type="button" className="btn btn--ghost btn--sm" aria-label={`Download ${d.title}`}
                      onClick={() => onDownload(d.pdfBase64, `${String(d.order).padStart(2, '0')}-${d.templateCode}.pdf`)}><Download size={14} /></button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {run.warnings.length > 0 && (
        <details className="card" style={{ marginBottom: 16 }}>
          <summary><strong>{run.warnings.length} build warning{run.warnings.length === 1 ? '' : 's'}</strong></summary>
          <ul style={{ fontSize: 12 }}>{run.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
        </details>
      )}

      <details className="card" style={{ marginBottom: 16 }}>
        <summary><strong>Considered and left out ({run.dropped.length})</strong></summary>
        <table className="table" style={{ marginTop: 8 }}>
          <thead><tr><th>Document</th><th>Rule</th><th>Why it is not in this package</th></tr></thead>
          <tbody>
            {run.dropped.map((d) => (
              <tr key={`${d.templateCode}-${d.stage}`} style={focusTemplate && d.templateCode.toUpperCase() === focusTemplate ? { background: 'var(--info-bg)' } : undefined}>
                <td><div>{d.title}</div><span className="pg-code">{d.templateCode}</span></td>
                <td>{STAGE_LABEL[d.stage] ?? d.stage}</td>
                <td>{d.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>

      {run.resolverNotes.length > 0 && (
        <details className="card">
          <summary><strong>Resolver notes ({run.resolverNotes.length})</strong></summary>
          <ul style={{ fontSize: 12 }}>{run.resolverNotes.map((n) => <li key={n}>{n}</li>)}</ul>
        </details>
      )}
    </>
  );
}

function VersionPill({ status }: { status: string | null }): JSX.Element | null {
  if (!status) return null;
  const cls = status === 'Active' ? 'pill--ok' : status === 'Draft' ? 'pill--warn' : 'pill--info';
  return <span className={`pill ${cls}`}>{status}</span>;
}

function DiffCard({ diff, baselineAt, canSideBySide, onSideBySide }: {
  diff: DiffRow[];
  baselineAt: string | null;
  canSideBySide: (row: DiffRow) => boolean;
  onSideBySide: (row: DiffRow) => void;
}): JSX.Element {
  const count = (k: DiffRow['kind']): number => diff.filter((r) => r.kind === k).length;
  const interesting = diff.filter((r) => r.kind !== 'same');
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <h3 style={{ flex: 1 }}>Compared with the previous run</h3>
        {baselineAt && <span className="pg-hint" style={{ margin: 0 }}>{new Date(baselineAt).toLocaleString()}</span>}
      </div>
      <div className="pg-diff-counts">
        <span className="pill pill--ok">{count('added')} added</span>
        <span className="pill pill--err">{count('removed')} removed</span>
        <span className="pill pill--warn">{count('changed')} changed</span>
        <span className="pill pill--muted">{count('same')} unchanged</span>
      </div>
      {interesting.length === 0 ? (
        <p className="pg-hint">Same documents with the same content as last time.</p>
      ) : (
        <table className="table pg-diff">
          <thead><tr><th>Document</th><th>Previous run</th><th>This run</th><th /></tr></thead>
          <tbody>
            {interesting.map((r) => {
              const d = r.after ?? r.before!;
              return (
                <tr key={r.key}>
                  <td><div>{d.title}</div><span className="pg-code">{d.templateCode}</span></td>
                  <td>{r.before ? `Version ${r.before.versionNumber}${r.before.versionStatus ? ` (${r.before.versionStatus})` : ''}` : <span className="pill pill--muted">Not in package</span>}</td>
                  <td>
                    {r.kind === 'added' && <span className="pill pill--ok">Added</span>}
                    {r.kind === 'removed' && <span className="pill pill--err">Removed</span>}
                    {r.kind === 'changed' && <><span className="pill pill--warn">Changed</span> {r.changes.join('; ')}</>}
                  </td>
                  <td>
                    {r.kind === 'changed' && canSideBySide(r) && (
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => onSideBySide(r)}><Columns2 size={14} /> Side by side</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
